import { Component, OnDestroy, OnInit } from '@angular/core';
import { combineLatest, Subject } from 'rxjs';
import { ActivatedRoute, Router } from '@angular/router';
import { takeUntil } from 'rxjs/operators';
import { FormBuilder, Validators } from '@angular/forms';
import { Store } from '@ngrx/store';
import { AppState } from '../../../app.reducer';
import * as fromLanding from '../../store/actions/landing.actions';
import * as landingSelector from '../../store/selectors/landing.selectors';
import { IProduct } from '../../../shared/interfaces/IProduct';
import { DomSanitizer } from '@angular/platform-browser';
import { LandingService } from '../../services/landing.service';
@Component({
  selector: 'app-view-product-page',
  templateUrl: './view-product-page.component.html',
  styleUrls: ['./view-product-page.component.scss'],
})
export class ViewProductPageComponent implements OnInit, OnDestroy {
  selectedPeriodicity: any;
  product_id: number;
  product: IProduct;
  destroy$: Subject<any>;
  zip_code: any = null;
  discount: any = null;
  images: any;
  loading = false;
  zipForm = this.formBuilder.group({
    zipCode: ['', [Validators.required]],
  });
  saleOrSubs: string;
  isSubsText: string;
  periodicity: number;
  purchaseType: string;
  quantity: number = 1;
  quantityOptions: number[] = [1, 5, 10, 20, 50];
  isQuantity: boolean = true;
  type: string;
  paymentMethod: string;
  productsForSubs: any[] = [];
  currentProductId!: number;
  noCoverage: boolean = false;
  has360Images: boolean = false;
  images360: string[] = [];
  constructor(
    private route: ActivatedRoute,
    private formBuilder: FormBuilder,
    private router: Router,
    private store: Store<AppState>,
    private _sanitizer: DomSanitizer,
    private landingService: LandingService,
  ) {
    this.destroy$ = new Subject<any>();
  }

  ngOnInit(): void {
    combineLatest([this.route.params, this.route.queryParams])
      .pipe(takeUntil(this.destroy$))
      .subscribe(([params, query]) => {
        this.product_id = parseInt(params.id, 10);
        this.currentProductId = this.product_id; // Actualizar el ID del producto actual
        this.type = query.type;
        this.zip_code = query.zipCode || null;
        this.discount = query.disc || null;

        this.loading = true;

        this.store.dispatch(
          fromLanding.getProduct({
            id: this.product_id,
            zipCode: this.zip_code,
          }),
        );

        // Actualizar productos relacionados cada vez que cambia el producto
        const allProducts = this.landingService.getproductsForSubs();
        this.productsForSubs = allProducts.filter(
          (p) => p.id !== this.currentProductId,
        );
      });
    this.store
      .select(landingSelector.selectProductDetail)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res) => {
        // La store puede devolver `null` o un objeto vacío `{}` cuando el código postal no tiene cobertura.
        // En esos casos queremos seguir mostrando la información del producto (obtenida sin zip)
        // pero indicar que no hay cobertura y deshabilitar los controles de compra.
        if (res && Object.keys(res).length > 0) {
          this.noCoverage = false;
          this.product = JSON.parse(JSON.stringify(res));
          this.loading = false;
          this.gotoTop();
          this.images = this.product.galeries;
          this.check360Images();
          // Proteger contra periodicity indefinido/nulo devuelto por la store
          if (
            this.product.periodicity &&
            Array.isArray(this.product.periodicity) &&
            this.product.periodicity.length > 0
          ) {
            this.product.periodicity.forEach((item) => {
              this.periodicity = item.id;
            });
          } else {
            // Asegurar que `periodicity` quede undefined cuando no esté presente
            this.periodicity = undefined;
          }
          if (this.type === 'sale') {
            this.purchaseType = 'Continuar';
            this.paymentMethod = 'Mercado Pago.';
            this.isQuantity = true;
          } else {
            this.purchaseType = 'Contratar';
            this.paymentMethod = 'Solo Tarjeta de Crédito';
            this.isQuantity = false;
          }
        } else if (this.zip_code) {
          // No se devolvió producto para el código postal dado -> sin cobertura
          this.noCoverage = true;
          this.loading = false;
          // Obtener un producto alternativo sin zipCode para poder
          // mostrar los detalles del producto al usuario.
          this.landingService.getProduct(this.product_id).subscribe(
            (fallback) => {
              this.product = JSON.parse(JSON.stringify(fallback));
              this.images = this.product.galeries;
              this.check360Images();
              if (
                this.product.periodicity &&
                Array.isArray(this.product.periodicity) &&
                this.product.periodicity.length > 0
              ) {
                this.product.periodicity.forEach((item) => {
                  this.periodicity = item.id;
                });
              } else {
                this.periodicity = undefined;
              }
            },
            (err) => {
              // Si la petición de fallback falla, limpiar `product` para evitar errores en la plantilla
              this.product = null as any;
            },
          );
        } else {
          // No hay zip code y no hay product -> limpiar estado
          this.noCoverage = false;
          this.product = JSON.parse(JSON.stringify(res));
          this.loading = false;
        }
      });
  }

  check360Images(): void {
    // Verificar si el producto tiene imágenes 360 por ID (o nombre como fallback)
    if (!this.product || !this.product_id) {
      this.has360Images = false;
      this.images360 = [];
      return;
    }

    type Product360Config = {
      folder: string;
      baseName: string;
      maxFrames?: number;
    };

    const productId360Map: { [key: number]: Product360Config } = {
      1602: { folder: 'compact360', baseName: 'compact', maxFrames: 144 },
      1606: { folder: 'deluxe360', baseName: 'deluxe', maxFrames: 145 },
      1607: { folder: 'ice360', baseName: 'ice', maxFrames: 145 },
      1603: { folder: 'eco360', baseName: 'eco', maxFrames: 145 },
      1605: { folder: 'bigbaby360', baseName: 'bigbaby', maxFrames: 145 },
      1604: { folder: 'baby360', baseName: 'baby', maxFrames: 145 },
    };

    const productName360Map: { [key: string]: Product360Config } = {
      compact: productId360Map[1602],
      deluxe: productId360Map[1606],
      ice: productId360Map[1607],
      ecofriendly: productId360Map[1603],
      eco: productId360Map[1603],
      bigbaby: productId360Map[1605],
      baby: productId360Map[1604],
    };

    const normalizedName = (this.product.name || '')
      .toLowerCase()
      .replace(/\s+/g, '');

    const product360Config =
      productId360Map[this.product_id] || productName360Map[normalizedName];

    if (product360Config) {
      this.images360 = [];
      const baseName = product360Config.baseName;
      const maxFrames = product360Config.maxFrames || 250;
      // Compact en disco es 0..144; el resto 1..145
      const startFrame = product360Config.folder === 'compact360' ? 0 : 1;
      const frameStep = 2;
      for (let i = startFrame; i <= maxFrames; i += frameStep) {
        this.images360.push(
          `/assets/${product360Config.folder}/${baseName}.${i}.webp`,
        );
      }

      this.has360Images = this.images360.length > 0;

      if (this.has360Images && this.images?.length > 0) {
        const already360 = this.images.some((img) => img.is360 === true);

        if (!already360) {
          // Miniatura = foto de galería (evita thumbs rotos/oscuros por webp sin MIME)
          const galleryThumb =
            this.images.find((img) => !!img?.file_name && !img.is360)
              ?.file_name || this.images360[0];

          this.images.push({
            file_name: galleryThumb,
            imageAlt: 'Vista 360°',
            is360: true,
          });
        }
      }
    } else {
      this.has360Images = false;
      this.images360 = [];
    }
  }

  gotoTop() {
    window.scroll({
      top: 0,
      left: 0,
      behavior: 'smooth',
    });
  }
  async onSubmit(): Promise<any> {
    const { zipCode } = this.zipForm.value;
    const discountParam = this.discount ? `&disc=${this.discount}` : '';
    await this.router.navigateByUrl(
      `/products/${this.product_id}?type=${this.type}&zipCode=${zipCode}${discountParam}`,
    );
    this.zipForm.reset();
  }
  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }
  selectPeriodicity(periodicity): void {
    this.selectedPeriodicity = periodicity;
    console.log(periodicity, 'periodicity');
    console.log(this.product, 'product');
  }
  gotoProduct(): void {
    this.product = null;
    this.zip_code = null;
    this.router.navigateByUrl(`/products/${this.product_id}?type=sub`);
  }
  purchaseProduct(): void {
    // Construye la URL base
    let url = `/purchase?product=${this.product_id}&plan=${this.periodicity}&quantity=${this.quantity}&type=${this.type}&zipCode=${this.zip_code}`;

    // Solo añade el descuento si this.discount tiene un valor
    if (this.discount) {
      url += `&disc=${this.discount}`;
    }

    this.router.navigateByUrl(url);
  }
  formatNumber(numeroOriginal: string): string {
    // Convierte el número de cadena a número de punto flotante (float)
    const numero = parseFloat(numeroOriginal);

    // Aplica el formato con tres decimales y usa '.' como separador de miles
    const numeroFormateado = numero.toLocaleString('es-AR', {
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    });

    return numeroFormateado;
  }
  /* getPrice(product: IProduct): { price: number, periodicity: string, cost: number} | null {
    if (!product.periodicity || product.periodicity.length === 0) {
      return null;
    }
    const monthly = product.periodicity.find(item => item.months >= 1);
    if (monthly) {
      if (monthly.months === 1) {
        return {
          price: monthly.pivot.periodic_price,
          cost: monthly.pivot.price,
          periodicity: 'mensuales'
        };
      }
      else {
        switch (monthly.months) {
          case 2: {
            return {
              price: monthly.pivot.periodic_price,
              cost: monthly.pivot.price,
              periodicity: 'por bimestre'
            };
          }
          case 3: {
            return {
              price: monthly.pivot.periodic_price,
              cost: monthly.pivot.price,
              periodicity: 'por trimestre'
            };
          }
          case 4: {
            return {
              price: monthly.pivot.periodic_price,
              cost: monthly.pivot.price,
              periodicity: 'por cuatrimestre'
            };
          }
          case 6: {
            return {
              price: monthly.pivot.periodic_price,
              cost: monthly.pivot.price,
              periodicity: 'por semestre'
            };
          }
          default: {
            return {
              price: monthly.pivot.periodic_price,
              cost: monthly.pivot.price,
              periodicity: `por ${monthly.months} meses`
            };
          }
        }
      }
    }
    const sale = product.periodicity.find(item => item.months === 0 && item.years === 0 && item.days === 0);
    if (sale) {
      return {
        price: monthly.pivot.periodic_price,
        cost: monthly.pivot.price,
        periodicity: `por única vez`
      };
    }
  } */

  getPrice(product: IProduct): {
    original_price: number;
    price: number;
    periodicity: string;
    original_cost: number;
    cost: number;
    periodicity_label: string;
  } | null {
    if (!product.periodicity || product.periodicity.length === 0) {
      return null;
    }

    if (
      product.periodicity &&
      product.periodicity.length > 0 &&
      this.type === 'sale'
    ) {
      this.saleOrSubs = 'Venta';
      let installation: number;
      let periodiPrice: number;
      product.periodicity.forEach((periodicity) => {
        //const periodiPrice = product.periodicity.find(p => p.name === 'Venta');
        if (
          periodicity &&
          periodicity.years === 0 &&
          periodicity.months === 0 &&
          periodicity.days === 0
        ) {
          installation = periodicity.pivot.price;
          periodiPrice = periodicity.pivot.periodic_price;
        }
      });
      return {
        original_price: product.periodicity[0].pivot.periodic_price,
        price: periodiPrice,
        cost: installation,
        original_cost: product.periodicity[0].pivot.price,
        periodicity: `por ${product.periodicity[0].months} meses`,
        periodicity_label: '',
      };
    }

    const monthly = product.periodicity.find((item) => item.months >= 1);
    const discount = this.discount;

    if (monthly) {
      let price = monthly.pivot.periodic_price; // Precio original
      let cost = monthly.pivot.price;
      let periodicity_label;
      this.saleOrSubs = 'Mensual';
      this.isSubsText = 'x mes';

      // Aplicar descuento si corresponde
      if (discount === 'CYBERFIL50') {
        price = price * 0.5; // Aplicar 50% de descuento
        cost = 0;
        periodicity_label = 'en tu primer mes';
      } else if (discount === 'CYBERFIL20') {
        price = price * 0.8;
        cost = 0;
        periodicity_label = 'en tus primeros 3 meses';
      }

      if (monthly.months === 1) {
        return {
          original_price: monthly.pivot.periodic_price,
          price: price,
          cost: cost,
          original_cost: monthly.pivot.price,
          periodicity: 'x mes',
          periodicity_label: periodicity_label,
        };
      } else {
        switch (monthly.months) {
          case 2: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: 'por bimestre',
              periodicity_label: '',
            };
          }
          case 3: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: 'por trimestre',
              periodicity_label: '',
            };
          }
          case 4: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: 'por cuatrimestre',
              periodicity_label: '',
            };
          }
          case 6: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: 'por semestre',
              periodicity_label: '',
            };
          }
          default: {
            return {
              original_price: monthly.pivot.periodic_price,
              price: price,
              cost: cost,
              original_cost: monthly.pivot.price,
              periodicity: `por ${monthly.months} meses`,
              periodicity_label: '',
            };
          }
        }
      }
    }
  }

  getSalePrice(
    product: IProduct,
    periodicityMonths: number,
  ): {
    price: number;
    cost: number;
    percentage: number;
  } | null {
    if (!product.periodicity || product.periodicity.length === 0) {
      return null;
    }

    const periodicity = product.periodicity.find(
      (p: any) => p.months === periodicityMonths,
    );
    if (periodicity) {
      return {
        price: periodicity.pivot.periodic_price,
        cost: periodicity.pivot.price,
        percentage: 40,
      };
    }
  }

  getPeriodicityName(product: IProduct): string {
    let periodicityName = '';

    if (
      product.periodicity &&
      product.periodicity.length > 0 &&
      this.type === 'sale'
    ) {
      for (const periodicity of product.periodicity) {
        if (
          periodicity &&
          periodicity.years === 0 &&
          periodicity.months === 0 &&
          periodicity.days === 0
        ) {
          return 'Venta';
        } else {
          return 'Mensual';
        }
      }
    }

    return periodicityName; // Default return value if no conditions match
  }

  increase() {
    this.quantity++;
  }

  decrease() {
    if (this.quantity > 0) this.quantity--;
  }

  setQuantity(event: Event) {
    const value = (event.target as HTMLSelectElement).value;
    this.quantity = Number(value);
  }

  calculateInterestFreeInstallments(
    price: number,
    installments: number = 6,
  ): string {
    if (!price || price <= 0 || !installments || installments <= 0) {
      return '0';
    }

    const installmentAmount = price / installments;
    return this.formatNumber(installmentAmount.toString());
  }
  productDetail(product: IProduct, type: 'sale' | 'sub' = 'sub'): void {
    this.loading = true;
    let queryParams: string[] = [];
    if (this.zip_code) {
      queryParams.push(`zipCode=${this.zip_code}`);
    }
    if (this.discount) {
      queryParams.push(`disc=${this.discount}`);
    }
    queryParams.push(`type=${type}`);
    const params = '?' + queryParams.join('&');

    this.router.navigateByUrl(`/products/${product.id}${params}`).then(() => {
      this.loading = false;
    });
  }

  getWhatsAppLink(productName: string): string {
    const message = `Hola, quiero más información sobre el modelo ${productName} para mi hogar`;
    return `https://wa.me/5491168786533?text=${encodeURIComponent(message)}`;
  }
}
